// src/features/giftCategoriesSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { AppDispatch } from "../app/store";
import { api } from "../utils/utils";

/* ------------ Types ------------ */
type AsyncStatus = "idle" | "loading" | "succeeded" | "failed";

export interface GiftCategoryInput {
  name: string;
  description: string;
  min_range: number;
  max_range: number;
  currency: string;
}

export interface GiftCategory {
  id: string;
  name: string;
  description: string;
  min_range: number;
  max_range: number;
  currency: string;
  created_at: string; // ISO8601
  updated_at: string; // ISO8601
}

export interface ValidationErrorItem {
  loc: (string | number)[];
  msg: string;
  type: string;
}

/* ------------ State ------------ */
interface GiftCategoriesState {
  // list
  listStatus: AsyncStatus;
  listError: string | null;
  isListInProgress: boolean;
  categories: GiftCategory[];

  // create/add
  createStatus: AsyncStatus;
  createError: string | null;
  isCreateInProgress: boolean;
  lastCreateResponse: GiftCategory | null;
  createValidationErrors: ValidationErrorItem[] | null;

  // delete
  deleteStatus: AsyncStatus;
  deleteError: string | null;
  isDeleteInProgress: boolean;
  lastDeleteResponse: string | null; // 200 returns a plain string
  deleteValidationErrors: ValidationErrorItem[] | null;

  // update
  updateStatus: AsyncStatus;
  updateError: string | null;
  isUpdateInProgress: boolean;
  lastUpdateResponse: GiftCategory | null;
  updateValidationErrors: ValidationErrorItem[] | null;
}

const initialState: GiftCategoriesState = {
  // list
  listStatus: "idle",
  listError: null,
  isListInProgress: false,
  categories: [],

  // create/add
  createStatus: "idle",
  createError: null,
  isCreateInProgress: false,
  lastCreateResponse: null,
  createValidationErrors: null,

  // delete
  deleteStatus: "idle",
  deleteError: null,
  isDeleteInProgress: false,
  lastDeleteResponse: null,
  deleteValidationErrors: null,

  // update
  updateStatus: "idle",
  updateError: null,
  isUpdateInProgress: false,
  lastUpdateResponse: null,
  updateValidationErrors: null,
};

/* ------------ Error helper ------------ */
function formatApiError(err: any): {
  status: number | null;
  message: string;
  validation?: ValidationErrorItem[];
} {
  if (err?.response) {
    const status = err.response.status as number;
    const data = err.response.data;

    // 422 validation array
    if (status === 422 && Array.isArray(data?.detail)) {
      const items = data.detail as ValidationErrorItem[];
      const msg = items.map(i => `${i.loc?.join(".")}: ${i.msg}`).join("; ");
      return { status, message: msg || "Validation failed", validation: items };
    }

    // 400/401/403/... with string detail or message
    const detail = data?.detail || data?.message || "Request failed";
    return { status, message: String(detail) };
  }

  return { status: null, message: "Network error. Please try again." };
}

/* ------------ Thunks ------------ */
export const fetchGiftCategories = createAsyncThunk<
  GiftCategory[],
  void,
  { dispatch: AppDispatch; rejectValue: { message: string } }
>("giftCategories/fetchGiftCategories", async (_void, { rejectWithValue }) => {
  try {
    const res = await api.get("/categories/list");
    return (res.data as GiftCategory[]) ?? [];
  } catch (error: any) {
    const { message } = formatApiError(error);
    return rejectWithValue({ message });
  }
});

export const addGiftCategory = createAsyncThunk<
  GiftCategory,
  GiftCategoryInput,
  { dispatch: AppDispatch; rejectValue: { message: string; validation?: ValidationErrorItem[] } }
>("giftCategories/addGiftCategory", async (payload, {dispatch, rejectWithValue }) => {
  try {
    const res = await api.post("/categories/add", payload);
    dispatch(fetchGiftCategories());
    return res.data as GiftCategory; // 200 returns the object
  } catch (error: any) {
    const { message, validation } = formatApiError(error);
    return rejectWithValue({ message, validation });
  }
});

export const deleteGiftCategory = createAsyncThunk<
  string, // 200 returns a plain string
  { category_id: number | string },
  { dispatch: AppDispatch; rejectValue: { message: string; validation?: ValidationErrorItem[] } }
>("giftCategories/deleteGiftCategory", async ({ category_id }, {dispatch, rejectWithValue }) => {
  try {
    const res = await api.delete("/categories/delete", {
      params: { category_id },
    });
    dispatch(fetchGiftCategories());
    return res.data as string; // success returns "string"
  } catch (error: any) {
    const { message, validation } = formatApiError(error);
    return rejectWithValue({ message, validation });
  }
});

export const updateGiftCategory = createAsyncThunk<
  GiftCategory,
  { category_id: number | string; data: GiftCategoryInput },
  { dispatch: AppDispatch; rejectValue: { message: string; validation?: ValidationErrorItem[] } }
>("giftCategories/updateGiftCategory", async ({ category_id, data }, { rejectWithValue, dispatch }) => {
  try {
    const res = await api.put("/categories/update", data, {
      params: { category_id },
    });
    // optional: keep list fresh
    // dispatch(fetchGiftCategories());
    return res.data as GiftCategory; // 200 returns the updated object
  } catch (error: any) {
    const { message, validation } = formatApiError(error);
    return rejectWithValue({ message, validation });
  }
});

/* ------------ Slice ------------ */
const giftCategoriesSlice = createSlice({
  name: "giftCategories",
  initialState,
  reducers: {
    // list resets
    resetListStatus(state) {
      state.listStatus = "idle";
      state.listError = null;
      state.isListInProgress = false;
    },
    clearCategories(state) {
      state.categories = [];
    },

    // create/add resets
    resetCreateStatus(state) {
      state.createStatus = "idle";
      state.createError = null;
      state.isCreateInProgress = false;
      state.createValidationErrors = null;
    },
    clearLastCreateResponse(state) {
      state.lastCreateResponse = null;
    },

    // delete resets
    resetDeleteStatus(state) {
      state.deleteStatus = "idle";
      state.deleteError = null;
      state.isDeleteInProgress = false;
      state.deleteValidationErrors = null;
    },
    clearLastDeleteResponse(state) {
      state.lastDeleteResponse = null;
    },

    // update resets
    resetUpdateStatus(state) {
      state.updateStatus = "idle";
      state.updateError = null;
      state.isUpdateInProgress = false;
      state.updateValidationErrors = null;
    },
    clearLastUpdateResponse(state) {
      state.lastUpdateResponse = null;
    },
  },
  extraReducers: (builder) => {
    // fetchGiftCategories
    builder
      .addCase(fetchGiftCategories.pending, (state) => {
        state.listStatus = "loading";
        state.listError = null;
        state.isListInProgress = true;
      })
      .addCase(fetchGiftCategories.fulfilled, (state, action: PayloadAction<GiftCategory[]>) => {
        state.listStatus = "succeeded";
        state.isListInProgress = false;
        state.categories = action.payload || [];
      })
      .addCase(fetchGiftCategories.rejected, (state, action) => {
        state.listStatus = "failed";
        state.isListInProgress = false;
        state.listError = action.payload?.message || action.error.message || "Failed to fetch gift categories";
      });

    // addGiftCategory
    builder
      .addCase(addGiftCategory.pending, (state) => {
        state.createStatus = "loading";
        state.createError = null;
        state.isCreateInProgress = true;
        state.createValidationErrors = null;
      })
      .addCase(addGiftCategory.fulfilled, (state, action: PayloadAction<GiftCategory>) => {
        state.createStatus = "succeeded";
        state.isCreateInProgress = false;
        state.lastCreateResponse = action.payload;
        // instantly reflect in list
        state.categories = [action.payload, ...state.categories];
      })
      .addCase(addGiftCategory.rejected, (state, action) => {
        state.createStatus = "failed";
        state.isCreateInProgress = false;
        state.createError = action.payload?.message || action.error.message || "Failed to add gift category";
        state.createValidationErrors = action.payload?.validation ?? null;
      });

    // deleteGiftCategory
    builder
      .addCase(deleteGiftCategory.pending, (state) => {
        state.deleteStatus = "loading";
        state.deleteError = null;
        state.isDeleteInProgress = true;
        state.deleteValidationErrors = null;
      })
      .addCase(deleteGiftCategory.fulfilled, (state, action: PayloadAction<string>) => {
        state.deleteStatus = "succeeded";
        state.isDeleteInProgress = false;
        state.lastDeleteResponse = action.payload;

        // If API returns the deleted ID as string, remove from list
        const deletedId = action.payload;
        if (deletedId) {
          state.categories = state.categories.filter((c) => c.id !== deletedId);
        }
      })
      .addCase(deleteGiftCategory.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.isDeleteInProgress = false;
        state.deleteError = action.payload?.message || action.error.message || "Failed to delete gift category";
        state.deleteValidationErrors = action.payload?.validation ?? null;
      });

    // updateGiftCategory
    builder
      .addCase(updateGiftCategory.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = null;
        state.isUpdateInProgress = true;
        state.updateValidationErrors = null;
      })
      .addCase(updateGiftCategory.fulfilled, (state, action: PayloadAction<GiftCategory>) => {
        state.updateStatus = "succeeded";
        state.isUpdateInProgress = false;
        state.lastUpdateResponse = action.payload;

        // sync categories list
        const idx = state.categories.findIndex((c) => c.id === action.payload.id);
        if (idx >= 0) state.categories[idx] = action.payload;
        else state.categories = [action.payload, ...state.categories];
      })
      .addCase(updateGiftCategory.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.isUpdateInProgress = false;
        state.updateError = action.payload?.message || action.error.message || "Failed to update gift category";
        state.updateValidationErrors = action.payload?.validation ?? null;
      });
  },
});

/* ------------ Exports (no selectors) ------------ */
export const {
  // list
  resetListStatus,
  clearCategories,
  // create
  resetCreateStatus,
  clearLastCreateResponse,
  // delete
  resetDeleteStatus,
  clearLastDeleteResponse,
  // update
  resetUpdateStatus,
  clearLastUpdateResponse,
} = giftCategoriesSlice.actions;

export default giftCategoriesSlice.reducer;
